/**
 * MergedCreateEditForm — edit UI for a merged create-action card.
 *
 * Composes the created entity's own fields (CreateActionFields, via
 * useCreateActionFields) with an editable target picker per association
 * (AssociationTargetEditor), and registers ONE combined save/cancel into the
 * accordion. On save it PATCHes the primary action's payload (if its fields
 * changed) and each association action whose target was swapped (new
 * target_entity_id) — so the whole card commits atomically from the user's
 * point of view.
 *
 * Rendered only when the merged card is in edit mode, so all hooks run
 * unconditionally here (MergedCreateActionCard has conditional early returns).
 */

import { useEffect, useLayoutEffect } from 'react';

import { HintText } from '@/lib/base/forms/hint-text';
import { cx } from '@/utils/cx';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { getStableActionKey } from '../../lib/proposals/utils/helpers';
import { useActionEdit } from './ActionAccordion';
import { AssociationEntityCard } from './AssociationEntityCard';
import { AssociationTargetEditor } from './AssociationTargetEditor';
import { getSearchableTargetType } from './associationTargets';
import { CreateActionFields } from './CreateActionFields';
import { MergedStageAssociationEditor } from './MergedStageAssociationEditor';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';
import { useMergedFormState } from './useMergedFormState';

interface MergedCreateEditFormProps {
  primaryAction: ProposedAction<ObjectCreatedEntityPayload>;
  mergedAssociations: MergedAssociation[];
  orgId: number;
  proposalId?: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
}

export function MergedCreateEditForm({
  primaryAction,
  mergedAssociations,
  orgId,
  proposalId,
  onUpdate,
}: MergedCreateEditFormProps) {
  const { setDirty, saveHandlerRef, cancelHandlerRef } = useActionEdit();
  const state = useMergedFormState({
    primaryAction,
    mergedAssociations,
    orgId,
    proposalId,
    onUpdate,
  });

  useEffect(() => {
    setDirty(state.isDirty);
  }, [state.isDirty, setDirty]);

  useLayoutEffect(() => {
    saveHandlerRef.current = state.save;
    cancelHandlerRef.current = state.cancel;
  });

  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3')}>
      {state.fieldKeys.length > 0 && (
        <CreateActionFields
          fieldKeys={state.fieldKeys}
          values={state.values}
          schema={state.schema}
          onChange={state.handleChange}
        />
      )}

      {state.editableAssocs.map(assoc => {
        const targetType = getSearchableTargetType(assoc);
        // Version-stable identity: a partial save can re-version this
        // association while the form stays open. Keying the row on the raw id
        // would remount the typeahead mid-edit, and the value/handler lookups
        // must use the same key as the row (see useMergedFormState).
        const assocKey = getStableActionKey(assoc.action);
        const showClearedError =
          state.submitAttempted && state.clearedIds.has(assocKey);
        return (
          <div key={assocKey} className='flex flex-col gap-1'>
            <AssociationTargetEditor
              assoc={assoc}
              orgId={orgId}
              value={state.editedTargets[assocKey] ?? null}
              onChange={state.handlersById[assocKey]}
              extraOptions={
                targetType
                  ? state.extraOptionsByType.get(targetType)
                  : undefined
              }
            />
            {showClearedError && (
              <HintText isInvalid className='mt-1'>
                Please pick a target or cancel to leave the current association.
              </HintText>
            )}
          </div>
        );
      })}

      {state.stageAssocs.map(assoc => (
        <MergedStageAssociationEditor
          key={getStableActionKey(assoc.action)}
          assoc={assoc}
          orgId={orgId}
          onChange={state.handleStageIdChange}
        />
      ))}

      {state.readOnlyAssocs.length > 0 && (
        <div className='space-y-2'>
          {state.readOnlyAssocs.map(assoc => (
            <AssociationEntityCard
              key={assoc.action.id}
              assoc={assoc}
              orgId={orgId}
              sourceEntityType={primaryAction.payload.entity_type_name}
            />
          ))}
        </div>
      )}
    </div>
  );
}
